import client from './client';

export interface LatestNewsPreviewItem {
  title?: string | null;
  published_date?: string | null;
  url?: string | null;
  has_full_text?: boolean;
  content_snippet?: string | null;
}

export interface LatestNewsDetail {
  company_id?: string;
  company_name?: string;
  corporate_status?: string | null;
  source_url?: string | null;
  feed_type?: string | null;
  lookback_days?: number | null;
  status?: string;
  press_releases_found?: number;
  latest_press_release_date?: string | null;
  latest_press_release_title?: string | null;
  items?: LatestNewsPreviewItem[];
  reason?: string | null;
  error?: string | null;
}

export type DocumentIntelligenceAnalysis = 'milestone' | 'pipeline' | 'corporate_transactions';

export interface DocumentIntelligenceAnalysisOptions {
  source_type: 'all' | 'press_release' | 'presentation';
  max_per_run: number;
  preview: boolean;
  force: boolean;
  backfill_all_company_history?: boolean;
}

export interface DocumentIntelligenceSpendAmount {
  cost_usd: number;
  calls: number;
}

export interface DocumentIntelligenceMonthSpend {
  month: string;
  total_cost_usd: number;
  total_calls: number;
  analyses: {
    milestone: DocumentIntelligenceSpendAmount;
    corporate_transactions: DocumentIntelligenceSpendAmount;
    pipeline: DocumentIntelligenceSpendAmount;
  };
}

export interface ScheduledJob {
  id: string;
  type: string;
  display_name: string;
  schedule: string;
  last_run_at: string | null;
  last_run_status: string;
  last_run_error: string | null;
  email_enabled: boolean;
  run_summary?: RunSummary;
  analyses?: DocumentIntelligenceAnalysis[];
  spend_mtd?: DocumentIntelligenceMonthSpend;
}

export interface RunSummary {
  companies_processed: number;
  companies_with_errors: number;
  companies_total?: number;
  companies_current?: number;
  log_lines: string[];
  /** Latest News Agent (and similar) fields when present in last_run_summary. */
  lookback_days?: number;
  companies_with_news?: number;
  press_releases_found?: number;
  scope?: string;
  details?: LatestNewsDetail[];
}

export interface ScheduledJobsResponse {
  environment: string;
  jobs: ScheduledJob[];
  /** Timezone for schedule times (e.g. "UTC"). All times are in this zone. */
  scheduler_timezone?: string;
}

export interface TriggerRunResponse {
  job_id: string;
  status: string;
  last_run_at: string | null;
  last_run_error: string | null;
  environment_used: string;
  run_summary?: RunSummary;
}

export interface ScheduledJobConfig {
  id: string;
  type: string;
  display_name: string;
  enabled: boolean;
  schedule: { type: string; time: string };
  email: { enabled: boolean; recipients: string[] };
  ignore_last_run?: boolean;
  analyses?: DocumentIntelligenceAnalysis[];
  analysis_options?: Partial<
    Record<DocumentIntelligenceAnalysis, DocumentIntelligenceAnalysisOptions>
  >;
  last_run_at: string | null;
  last_run_status: string;
  last_run_error: string | null;
}

export interface JobUpdateBody {
  enabled?: boolean;
  schedule?: { type?: string; time?: string };
  email?: { enabled?: boolean; recipients?: string[] };
  ignore_last_run?: boolean;
  analyses?: DocumentIntelligenceAnalysis[];
  analysis_options?: Partial<
    Record<DocumentIntelligenceAnalysis, Partial<DocumentIntelligenceAnalysisOptions>>
  >;
}

export interface ScheduledJobStatus {
  job_id: string;
  last_run_status: string;
  last_run_at: string | null;
}

export interface PrCoverageGapDetail {
  company_id?: string;
  company_name?: string;
  corporate_status?: string | null;
  source_url?: string | null;
  feed_type?: string | null;
  page_last_pr_date?: string | null;
  neo4j_last_pr_date?: string | null;
  gap_days?: number | null;
  status?: string;
  http_status?: number | null;
  reason?: string | null;
  proposed_url?: string | null;
  latest_press_release_title?: string | null;
  error?: string | null;
}

export interface PrCoverageGapLatestSummary {
  job_id: string;
  last_run_status: string;
  last_run_at: string | null;
  environment?: string | null;
  scope?: string | null;
  companies_scanned?: number;
  companies_in_scope?: number;
  companies_processed?: number;
  companies_with_errors?: number;
  gap_threshold_days?: number | null;
  stale_days?: number | null;
  status_counts: Record<string, number>;
  details: PrCoverageGapDetail[];
  deferred?: boolean;
  default_expanded_statuses?: string[];
}

export const getScheduledJobs = async (): Promise<ScheduledJobsResponse> => {
  const response = await client.get<ScheduledJobsResponse>('/api/v1/scheduled-jobs');
  return response.data;
};

/**
 * Lightweight, unauthenticated status for a single job (last run time + status).
 * Used by non-admin pages (e.g. the completion dashboard) to show when a sweep
 * last ran without requiring the editor-restricted jobs list endpoint.
 */
export const getScheduledJobStatus = async (
  jobId: string
): Promise<ScheduledJobStatus> => {
  const response = await client.get<ScheduledJobStatus>(
    `/api/v1/scheduled-jobs/${jobId}/status`
  );
  return response.data;
};

export const getPrCoverageGapLatestSummary = async (
  jobId: string = 'pr_coverage_gap_agent'
): Promise<PrCoverageGapLatestSummary> => {
  const response = await client.get<PrCoverageGapLatestSummary>(
    `/api/v1/scheduled-jobs/${jobId}/latest-summary`
  );
  return response.data;
};

export interface LatestNewsLatestSummary {
  job_id: string;
  last_run_status: string;
  last_run_at: string | null;
  environment?: string | null;
  scope?: string | null;
  lookback_days?: number | null;
  companies_scanned?: number;
  companies_in_scope?: number;
  companies_processed?: number;
  companies_with_errors?: number;
  companies_with_news?: number;
  press_releases_found?: number;
  status_counts: Record<string, number>;
  details: LatestNewsDetail[];
  deferred?: boolean;
  default_expanded_statuses?: string[];
}

/** Latest News Agent run (lookback scan + per-company PR lists). */
export const getLatestNewsLatestSummary = async (
  jobId: string = 'latest_news_agent'
): Promise<LatestNewsLatestSummary> => {
  const response = await client.get<LatestNewsLatestSummary>(
    `/api/v1/scheduled-jobs/${jobId}/latest-summary`
  );
  return response.data;
};

export const getScheduledJob = async (
  jobId: string
): Promise<ScheduledJobConfig> => {
  const response = await client.get<ScheduledJobConfig>(
    `/api/v1/scheduled-jobs/${jobId}`
  );
  return response.data;
};

export const updateScheduledJob = async (
  jobId: string,
  data: JobUpdateBody
): Promise<ScheduledJobConfig> => {
  const response = await client.put<ScheduledJobConfig>(
    `/api/v1/scheduled-jobs/${jobId}`,
    data
  );
  return response.data;
};

export const updateScheduledJobsConfig = async (data: {
  environment: string;
}): Promise<{ environment: string }> => {
  const response = await client.put<{ environment: string }>(
    '/api/v1/scheduled-jobs/config',
    data
  );
  return response.data;
};

export interface RunOptions {
  environment?: string;
  days_back?: number;
  send_email?: boolean;
  overwrite?: boolean;
  company_id?: string;
  ignore_last_run?: boolean;
  /** Company Completion Agent only: which companies to include. */
  scope?: 'public' | 'non_public' | 'all';
  /** Company Completion Agent only: when true, assess only and write nothing. */
  dry_run?: boolean;
  /** Cap companies processed (health-check). 0 = full sweep / unlimited. */
  max_per_run?: number;
  /** Gap / latest-news agents: filter by coverage_priority. */
  coverage_priority?: 'high' | 'medium' | 'low' | 'all';
  /** Gap agent: concurrent company workers. */
  workers?: number;
  /** Gap agent: hunt replacement URLs after unreachable listings. */
  enable_url_repair_check?: boolean;
}

export interface TriggerRunStartedResponse {
  status: 'running';
  job_id: string;
  message: string;
}

export const triggerJobRun = async (
  jobId: string,
  options?: RunOptions,
  signal?: AbortSignal
): Promise<TriggerRunStartedResponse> => {
  const body =
    options && Object.keys(options).length > 0
      ? Object.fromEntries(
          Object.entries(options).filter(
            ([, v]) => v !== undefined && v !== null
          )
        )
      : {};
  const response = await client.post<TriggerRunStartedResponse>(
    `/api/v1/scheduled-jobs/${jobId}/run`,
    body,
    { signal }
  );
  return response.data;
};

/** Build TriggerRunResponse from a job (for polling). */
export function jobToRunResult(
  job: ScheduledJob,
  environment: string
): TriggerRunResponse {
  const summary = job.run_summary;
  return {
    job_id: job.id,
    status: job.last_run_status,
    last_run_at: job.last_run_at,
    last_run_error: job.last_run_error ?? null,
    environment_used: environment,
    run_summary: summary,
  };
}
